// frontend/src/components/DegreeAudit/RequirementDisplays/RequirementRenderer.tsx
// v2.3 - Simplified audit view

import React, { memo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

import AuditRequirementDisplay, {
  type AuditRequirementNode,
} from './AuditRequirementDisplay';

interface RequirementErrorFallbackProps {
  requirement: AuditRequirementNode | null | undefined;
  error: Error | null;
}

const RequirementErrorFallback = ({ requirement, error }: RequirementErrorFallbackProps) => (
  <div className="bg-red-50 border border-red-200 rounded-lg p-3 md:p-4">
    <div className="flex items-start space-x-2">
      <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
      <div className="text-sm">
        <div className="font-medium text-red-800 mb-1">
          Error rendering requirement: {requirement?.name || 'Unknown'}
        </div>
        <div className="text-red-700">
          Type: {requirement?.requirement?.type || 'Unknown'}
        </div>
        {process.env.NODE_ENV === 'development' && (
          <div className="text-xs text-red-600 mt-2 font-mono">
            {error?.message || 'Unknown error'}
          </div>
        )}
      </div>
    </div>
  </div>
);

interface RequirementErrorBoundaryProps {
  requirement: AuditRequirementNode | null | undefined;
  children: ReactNode;
}

interface RequirementErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class RequirementErrorBoundary extends React.Component<
  RequirementErrorBoundaryProps,
  RequirementErrorBoundaryState
> {
  constructor(props: RequirementErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): RequirementErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('RequirementRenderer Error:', error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <RequirementErrorFallback
          requirement={this.props.requirement}
          error={this.state.error}
        />
      );
    }
    return this.props.children;
  }
}

export interface RequirementRendererProps {
  requirement: AuditRequirementNode;
}

/**
 * Simplified audit view that displays requirement status.
 * Wraps AuditRequirementDisplay with an error boundary so a single bad node
 * doesn't blank out the whole audit.
 */
const RequirementRenderer = memo(({ requirement }: RequirementRendererProps) => {
  return (
    <RequirementErrorBoundary requirement={requirement}>
      <AuditRequirementDisplay requirement={requirement} />
    </RequirementErrorBoundary>
  );
});

RequirementRenderer.displayName = 'RequirementRenderer';

export default RequirementRenderer;
